/**
 * [INPUT]: 无运行时依赖（纯数学）
 * [OUTPUT]: 对外提供 piecewise(value, params) 与 PiecewiseParams 类型
 * [POS]: apps/web/src/scorer 的分段线性映射器，将原始几何量映射到 [0,peak] 子分；对齐 docs §4.3 归一化
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 */

export interface PiecewiseParams {
  /** 理想区间下界（含）。value ∈ [ideal_low, ideal_high] 时子分 = peak */
  ideal_low: number
  /** 理想区间上界（含）。null 表示无上界（越高越好） */
  ideal_high: number | null
  /** 下侧软边界：value 降至 soft_min 时子分 = 0。null 表示无下侧衰减 */
  soft_min: number | null
  /** 上侧软边界：value 升至 soft_max 时子分 = 0。null 表示无上侧衰减 */
  soft_max: number | null
  /** 理想带内子分（<1，默认 0.55）：落入理想区间即得 peak，不再顶满 1.0；顶端稀有性靠 scorer 的协同 bonus 表达 */
  peak?: number
}

/** peak 缺省值：理想带内子分。设计上 < 1，使"在正常区间"不再等价于"完美"。 */
export const DEFAULT_PEAK = 0.55

/**
 * 分段线性映射 → [0, peak]：
 *  - value ∈ [ideal_low, ideal_high] → peak
 *  - value < ideal_low → 自 ideal_low 线性衰减至 soft_min = 0（按 peak 缩放）；越过 soft_min 为 0
 *  - value > ideal_high → 自 ideal_high 线性衰减至 soft_max = 0（按 peak 缩放）；越过 soft_max 为 0
 *  - ideal_high = null：仅下侧衰减（越高越好，如 symmetry），越过 ideal_low 保持 peak
 *  - ideal_low = null：仅上侧衰减（越低越好）
 */
export function piecewise(value: number, p: PiecewiseParams): number {
  const peak = p.peak ?? DEFAULT_PEAK
  const lo = p.ideal_low
  const hi = p.ideal_high

  // 理想区间内
  if (value >= lo && (hi === null || value <= hi)) return peak

  // 下侧衰减
  if (value < lo) {
    if (p.soft_min === null) return peak
    if (value <= p.soft_min) return 0
    const span = lo - p.soft_min
    return span <= 0 ? 0 : peak * ((value - p.soft_min) / span)
  }

  // 上侧衰减（value > ideal_high）
  if (hi === null) return peak
  if (p.soft_max === null) return peak
  if (value >= p.soft_max) return 0
  const span = p.soft_max - hi
  return span <= 0 ? 0 : peak * ((p.soft_max - value) / span)
}